import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchUser } from "@/api/community";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Card } from "@/components/ui/card";

interface MentionListProps {
  items: SearchUser[];
  command: (item: {
    id: string;
    label: string;
    userType?: string;
    organizationId?: string;
  }) => void;
}

export const MentionList = forwardRef<any, MentionListProps>((props, ref) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({
        id: item.id,
        label: item.full_name,
        userType: item.type,
        organizationId: item.organization_id,
      });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    const nameParts = name.trim().split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };

  if (props.items.length === 0) {
    return null;
  }

  return (
    <Card className="z-50 w-64 border shadow-lg">
      <Command>
        <CommandList>
          <CommandEmpty>{t("editor.no_users_found")}</CommandEmpty>
          <CommandGroup heading={t("editor.suggestions")}>
            {props.items.map((user, index) => (
              <CommandItem
                key={user.id}
                onSelect={() => selectItem(index)}
                className={`cursor-pointer ${index === selectedIndex ? "bg-accent" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8 rounded-full">
                    <AvatarImage src={user.photo_thumbnail_url || ""} />
                    <AvatarFallback className="bg-brand-accent text-primary text-xs font-semibold">
                      {getUserInitials(user.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{user.full_name}</div>
                    <div className="text-muted-foreground text-xs">{user.email}</div>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </Card>
  );
});

MentionList.displayName = "MentionList";
